/**
 * "Today's focus": the server's daily focus items when signed in, or items derived locally from the engine when
 * offline. Both become the same FocusItem shape so the card renders one way.
 */
import type { ContactRow, DailyFocusItem, ReconnectSuggestion } from "@hub/shared";

export type FocusKind = DailyFocusItem["itemType"];

export interface FocusItem {
  id: string;
  kind: FocusKind;
  contactId: string | null;
  matchId: string | null;
  title: string;
  body: string | null;
  /** Where the item came from; local items cannot be completed on the server. */
  source: "server" | "local";
}

/** The user's calendar day as YYYY-MM-DD. The engine counts days in UTC unless told otherwise, and a day's picks should turn over at local midnight, not at 7 PM Central. */
export function localDay(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** When a snoozed reconnect comes back: `days` from now at 9 in the morning, local time. */
export function snoozeUntil(days: number, now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

export const FOCUS_KIND_LABELS: Record<FocusKind, string> = {
  reconnect: "Reconnect",
  enrich: "Add details",
  intro: "Introduction",
  celebrate: "Celebrate",
};

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/** Maps a server item to the card shape. `names` resolves contact ids to display names for the title. */
export function fromServerItem(item: DailyFocusItem, names: Record<string, string> = {}): FocusItem {
  const name = (item.contactId && names[item.contactId]) || str(item.data.contactName) || str(item.data.name) || null;
  const explicitTitle = str(item.data.title);
  // Introductions carry the card's subtitle ("Needs: CPA | Brandt Marino can help"); the other kinds a reason.
  const body = str(item.data.reason) ?? str(item.data.body) ?? str(item.data.description) ?? str(item.data.subtitle);
  let title: string;
  if (explicitTitle) title = explicitTitle;
  else if (item.itemType === "reconnect") title = name ? `Reconnect with ${name}` : "Reconnect with someone";
  else if (item.itemType === "enrich") title = name ? `Add what ${name} needs or offers` : "Add needs and offers to a person";
  else if (item.itemType === "intro") title = name ? `An introduction involving ${name} is waiting` : "An introduction is waiting on you";
  else title = name ? `Celebrate ${name}` : "Something to celebrate";
  return { id: item.id, kind: item.itemType, contactId: item.contactId, matchId: item.matchId, title, body, source: "server" };
}

export function fromServerItems(items: DailyFocusItem[], names: Record<string, string> = {}): FocusItem[] {
  return items.filter((i) => i.status === "pending").map((i) => fromServerItem(i, names));
}

export interface LocalFocusInput {
  suggestions: (ReconnectSuggestion & { contacts: ContactRow[] }) | null | undefined;
  /** Candidate rows for the "enrich" items: close people without needs or offers on file. */
  candidates: ContactRow[];
  waitingMatches: number;
  max?: number;
  /** Rows of the "enrich" kind at most; the engine's daily pick already limits them, this is the card's own cap. */
  maxEnrich?: number;
}

/** Local items: introductions waiting, then the day's reconnect pick, then the day's pick of close people without needs or offers. */
export function buildLocalFocus(input: LocalFocusInput): FocusItem[] {
  const max = input.max ?? 4;
  const maxEnrich = input.maxEnrich ?? 2;
  const out: FocusItem[] = [];
  if (input.waitingMatches > 0) {
    out.push({ id: "local-intro", kind: "intro", contactId: null, matchId: null, title: input.waitingMatches === 1 ? "An introduction is waiting on you" : `${input.waitingMatches} introductions are waiting on you`, body: "Accept, share or answer from Introductions.", source: "local" });
  }
  for (const c of input.suggestions?.contacts ?? []) {
    if (out.length >= max) break;
    const member = input.suggestions?.members.find((m) => m.contactId === c.contactId);
    out.push({ id: `local-reconnect-${c.contactId}`, kind: "reconnect", contactId: c.contactId, matchId: null, title: `Reconnect with ${c.displayName}`, body: member?.reason ?? c.headline ?? null, source: "local" });
  }
  const seen = new Set(out.map((o) => o.contactId));
  const enrich = input.candidates.filter((c) => !c.isArchived && !c.hasNeeds && !c.hasServices && !seen.has(c.contactId)).sort((a, b) => b.score - a.score || a.displayName.localeCompare(b.displayName));
  let enriched = 0;
  for (const c of enrich) {
    if (out.length >= max || enriched >= maxEnrich) break;
    enriched += 1;
    out.push({ id: `local-enrich-${c.contactId}`, kind: "enrich", contactId: c.contactId, matchId: null, title: `Add what ${c.displayName} needs or offers`, body: c.headline ?? "Hub can only match people whose needs and offers it knows.", source: "local" });
  }
  return out;
}

/** Copy for the credit balance chip. */
export function creditBalanceLabel(creditsRemaining: number | null | undefined): string {
  if (creditsRemaining === null || creditsRemaining === undefined) return "Unlimited";
  return `${creditsRemaining} ${creditsRemaining === 1 ? "credit" : "credits"}`;
}
